import React, { FunctionComponent, useState, useEffect } from 'react'
import { startCase } from 'lodash'
import { ColDef, GridApi, GridReadyEvent, ICellRendererParams } from 'ag-grid-community'
import { HStack, Link, Spinner, Text, Tooltip } from '@chakra-ui/react'

import Error from 'components/Toolkit/Text/Error'
import { formatUTCDate } from 'lib/date-time'
import { BaseGrid } from 'components/TailwindUIToolkit'
import SetFilter from 'components/AgGrid/filters/SetFilter'
import {
  usePortfolioHomeSystemWorkOrdersQuery,
  usePortfolioHomeSystemWorkOrdersByPortfolioHomeIdQuery
} from 'graphql/generated'
import { getWarrantyIcon, getWarrantyStatus } from 'lib/portfolio-home'
import { LINK_COLOR } from 'constants/colors'

// Union type for both query results
type PortfolioHomeSystemWorkOrder = NonNullable<
  | ReturnType<typeof usePortfolioHomeSystemWorkOrdersQuery>[0]['data']
  | ReturnType<typeof usePortfolioHomeSystemWorkOrdersByPortfolioHomeIdQuery>[0]['data']
>['portfolio_home_system_work_orders'][0]

interface SystemWorkOrderGridProps {
  portfolioHomeId?: string
  warrantyFilter: string
  serviceDateFilter: string
}

const getColumns = (address: boolean): ColDef<PortfolioHomeSystemWorkOrder>[] => [
  {
    colId: 'address',
    headerName: 'Address',
    valueGetter: (params) => {
      const line1 = params.data?.portfolio_home?.home?.address?.display_line_1 || ''
      const line2 = params.data?.portfolio_home?.home?.address?.display_line_2 || ''
      return `${line1}, ${line2}`
    },
    cellRenderer: (params: ICellRendererParams<PortfolioHomeSystemWorkOrder>) => {
      const line1 = params.data?.portfolio_home?.home?.address?.display_line_1 || ''
      const line2 = params.data?.portfolio_home?.home?.address?.display_line_2 || ''
      return (
        <Link isExternal color={LINK_COLOR} href={`/home/${params.data?.portfolio_home?.id}`}>
          {`${line1}, ${line2}`}
        </Link>
      )
    },
    resizable: true,
    hide: address
  },
  {
    colId: 'system',
    headerName: 'System',
    field: 'system',
    valueFormatter: (params) => startCase(params.value),
    filter: SetFilter,
    resizable: true
  },
  {
    colId: 'type',
    headerName: 'Type',
    field: 'type',
    valueFormatter: (params) => startCase(params.value),
    filter: SetFilter,
    resizable: true
  },
  {
    colId: 'under_warranty',
    headerName: 'Under Warranty',
    field: 'under_warranty',
    valueGetter: (params) => {
      return params.data?.under_warranty === true
        ? 'Yes'
        : params.data?.under_warranty === false
        ? 'No'
        : ''
    },
    filter: SetFilter,
    resizable: true
  },
  {
    colId: 'vendor',
    headerName: 'Vendor',
    field: 'vendor.name',
    valueFormatter: (params) => params.value,
    filter: SetFilter,
    resizable: true
  },
  {
    colId: 'service_date',
    headerName: 'Service Date',
    field: 'service_date',
    valueGetter: (params) =>
      params.data?.service_date ? new Date(params.data?.service_date) : null,
    valueFormatter: (params) => formatUTCDate(params.value, 'MMMM d, yyyy'),
    filter: 'agDateColumnFilter',
    resizable: true
  },
  {
    colId: 'warranty_end_date',
    headerName: 'Warranty End Date',
    field: 'warranty_end_date',
    valueGetter: (params) =>
      params.data?.warranty_end_date ? new Date(params.data.warranty_end_date) : null,
    cellRenderer: (params: ICellRendererParams<PortfolioHomeSystemWorkOrder>) => {
      const icon = getWarrantyIcon(params.value)
      const formattedDate = params.value ? formatUTCDate(params.value, 'MMMM d, yyyy') : ''
      const warrantyStatus = getWarrantyStatus(params.value)

      return (
        <Tooltip
          label={startCase(warrantyStatus || '')}
          placement='top'
          bg='white'
          color={
            warrantyStatus === 'expired'
              ? 'red.500'
              : warrantyStatus === 'expiring_soon'
              ? 'orange.500'
              : 'green.500'
          }
          fontSize='xs'
          p={1}
          borderRadius='md'
        >
          <HStack spacing={2}>
            {icon}
            <span>{formattedDate}</span>
          </HStack>
        </Tooltip>
      )
    },
    filter: 'agDateColumnFilter',
    resizable: true
  },
  {
    colId: 'warranty_status',
    headerName: 'Warranty Status',
    field: 'warranty_end_date',
    valueGetter: (params) => getWarrantyStatus(params.data?.warranty_end_date) || '',
    hide: true
  },
  {
    colId: 'notes',
    headerName: 'Notes',
    field: 'notes',
    valueFormatter: (params) => params.value,
    resizable: true
  }
]

const SystemWorkOrderGrid: FunctionComponent<React.PropsWithChildren<SystemWorkOrderGridProps>> = ({
  portfolioHomeId,
  warrantyFilter,
  serviceDateFilter
}) => {
  const [gridApi, setGridApi] = useState<GridApi>()

  const filterWarranties = (status: string) => {
    if (gridApi) {
      const warrantyFilterInstance = gridApi.getFilterInstance('warranty_status')
      if (warrantyFilterInstance) {
        if (status === 'all') {
          warrantyFilterInstance.setModel(null)
        } else {
          warrantyFilterInstance.setModel({
            type: 'contains',
            filter: [status]
          })
        }
        gridApi.onFilterChanged()
      }
    }
  }
  const filterServiceDates = (status: string) => {
    if (gridApi) {
      const serviceDateFilterInstance = gridApi.getFilterInstance('service_date')

      if (serviceDateFilterInstance) {
        if (status === 'all') {
          serviceDateFilterInstance.setModel(null)
        } else if (status === 'past_week') {
          const weekAgo = new Date()
          weekAgo.setDate(weekAgo.getDate() - 7)
          serviceDateFilterInstance.setModel({
            type: 'inRange',
            dateFrom: weekAgo.toISOString().split('T')[0],
            dateTo: new Date().toISOString().split('T')[0]
          })
        } else if (status === 'past_month') {
          const monthAgo = new Date()
          monthAgo.setDate(monthAgo.getDate() - 30)
          serviceDateFilterInstance.setModel({
            type: 'inRange',
            dateFrom: monthAgo.toISOString().split('T')[0],
            dateTo: new Date().toISOString().split('T')[0]
          })
        }

        gridApi.onFilterChanged()
      }
    }
  }

  useEffect(() => {
    filterWarranties(warrantyFilter)
  }, [warrantyFilter])

  useEffect(() => {
    filterServiceDates(serviceDateFilter)
  }, [serviceDateFilter])

  const portfolioHomeQuery = usePortfolioHomeSystemWorkOrdersByPortfolioHomeIdQuery({
    variables: {
      portfolioHomeId: portfolioHomeId!
    },
    pause: !portfolioHomeId
  })
  const recentQuery = usePortfolioHomeSystemWorkOrdersQuery()
  const [{ data, error, fetching }] = portfolioHomeId ? portfolioHomeQuery : recentQuery

  if (error) {
    return <Error>{error.message}</Error>
  }
  if (fetching) {
    return <Spinner />
  }

  const systemWorkOrders = data?.portfolio_home_system_work_orders

  return systemWorkOrders ? (
    <BaseGrid<PortfolioHomeSystemWorkOrder>
      columns={getColumns(!!portfolioHomeId)}
      rowData={systemWorkOrders}
      onGridReady={(evt: GridReadyEvent) => {
        setGridApi(evt.api)
      }}
    />
  ) : (
    <Text>No system work orders found</Text>
  )
}

export default SystemWorkOrderGrid
